<template>
	<div
		v-show="visible"
		ref="panel"
		class="GoodsStatsFloat"
		:style="panelStyle"
	>
		<div class="gsf-header" @mousedown.prevent="onDragStart">
			<div class="gsf-title">
				<span class="gsf-title-main">统计</span>
				<span class="gsf-title-sub">产品 <span class="num">{{ typeText }}</span> 类</span>
			</div>
			<button
				type="button"
				class="gsf-close"
				title="关闭"
				@mousedown.stop
				@click.stop="close"
			>×</button>
		</div>
		<div class="gsf-body">
			<div v-if="detailRows.length" class="gsf-form">
				<div
					v-for="g in detailRows"
					:key="g.kind + '-' + g.key"
					class="gsf-row"
				>
					<div class="gsf-name" :title="g.label">{{ g.label }}</div>
					<div class="gsf-fields">
						<div class="gsf-field gsf-field--length">
							<template v-if="g.kind === 'length'">
								<span class="gsf-field-label">总长度</span>
								<span class="num">{{ g.lengthText }}</span>
								<span class="gsf-field-unit">mm</span>
							</template>
						</div>
						<div class="gsf-field gsf-field--qty">
							<span class="gsf-field-label">共</span>
							<span class="num">{{ g.qtyText }}</span>
							<span class="gsf-field-unit">{{ g.kind === 'length' ? '支' : '个' }}</span>
						</div>
					</div>
				</div>
			</div>
			<div v-else class="gsf-empty">暂无统计数据</div>
		</div>
		<div
			class="gsf-resize"
			title="拖动调整大小"
			@mousedown.prevent="onResizeStart"
		></div>
	</div>
</template>

<script>
const STORAGE_KEY = 'gyl_goods_stats_float'

function clamp(n, min, max) {
	return Math.min(max, Math.max(min, n))
}

function defaultGeom() {
	const width = 440
	const height = 320
	const left = Math.max(16, window.innerWidth - width - 24)
	const top = Math.max(16, window.innerHeight - height - 24)
	return { left, top, width, height }
}

function loadGeom() {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY)
		if (!raw) return defaultGeom()
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') return defaultGeom()
		const base = defaultGeom()
		return {
			left: Number(parsed.left) || base.left,
			top: Number(parsed.top) || base.top,
			width: Number(parsed.width) || base.width,
			height: Number(parsed.height) || base.height
		}
	} catch (e) {
		return defaultGeom()
	}
}

export default {
	name: 'GoodsStatsFloat',
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		stats: {
			type: Object,
			default() {
				return {}
			}
		}
	},
	data() {
		const geom = typeof window !== 'undefined' ? loadGeom() : { left: 24, top: 24, width: 440, height: 320 }
		return {
			left: geom.left,
			top: geom.top,
			width: geom.width,
			height: geom.height,
			dragging: false,
			resizing: false,
			_dragOrigin: null,
			_resizeOrigin: null
		}
	},
	computed: {
		isMobile() {
			return typeof window !== 'undefined' && window.innerWidth <= 768
		},
		panelStyle() {
			if (this.isMobile) {
				return {
					left: '0',
					right: '0',
					bottom: '0',
					top: 'auto',
					width: '100%',
					height: 'auto',
					maxHeight: 'min(50vh, 360px)'
				}
			}
			return {
				left: this.left + 'px',
				top: this.top + 'px',
				width: this.width + 'px',
				height: this.height + 'px'
			}
		},
		typeText() {
			const s = this.stats || {}
			return s.productTypeText != null ? s.productTypeText : '0'
		},
		detailRows() {
			const s = this.stats || {}
			return Array.isArray(s.detailRows) ? s.detailRows : []
		}
	},
	watch: {
		visible(val) {
			if (val) this.$nextTick(this.clampIntoViewport)
		}
	},
	mounted() {
		window.addEventListener('mousemove', this.onPointerMove)
		window.addEventListener('mouseup', this.onPointerUp)
		window.addEventListener('resize', this.clampIntoViewport)
		this.clampIntoViewport()
	},
	beforeDestroy() {
		window.removeEventListener('mousemove', this.onPointerMove)
		window.removeEventListener('mouseup', this.onPointerUp)
		window.removeEventListener('resize', this.clampIntoViewport)
	},
	methods: {
		close() {
			this.$emit('update:visible', false)
		},
		persistGeom() {
			try {
				sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
					left: this.left,
					top: this.top,
					width: this.width,
					height: this.height
				}))
			} catch (e) { /* ignore */ }
		},
		clampIntoViewport() {
			if (this.isMobile) return
			const minW = 300
			const minH = 180
			const maxW = Math.max(minW, window.innerWidth - 16)
			const maxH = Math.max(minH, window.innerHeight - 16)
			this.width = clamp(this.width, minW, maxW)
			this.height = clamp(this.height, minH, maxH)
			this.left = clamp(this.left, 8, Math.max(8, window.innerWidth - this.width - 8))
			this.top = clamp(this.top, 8, Math.max(8, window.innerHeight - this.height - 8))
		},
		onDragStart(e) {
			if (this.isMobile) return
			if (e.button !== 0) return
			this.dragging = true
			this._dragOrigin = {
				x: e.clientX,
				y: e.clientY,
				left: this.left,
				top: this.top
			}
		},
		onResizeStart(e) {
			if (this.isMobile) return
			if (e.button !== 0) return
			this.resizing = true
			this._resizeOrigin = {
				x: e.clientX,
				y: e.clientY,
				width: this.width,
				height: this.height
			}
		},
		onPointerMove(e) {
			if (this.dragging && this._dragOrigin) {
				const dx = e.clientX - this._dragOrigin.x
				const dy = e.clientY - this._dragOrigin.y
				this.left = this._dragOrigin.left + dx
				this.top = this._dragOrigin.top + dy
				this.clampIntoViewport()
			} else if (this.resizing && this._resizeOrigin) {
				const dx = e.clientX - this._resizeOrigin.x
				const dy = e.clientY - this._resizeOrigin.y
				this.width = this._resizeOrigin.width + dx
				this.height = this._resizeOrigin.height + dy
				this.clampIntoViewport()
			}
		},
		onPointerUp() {
			if (this.dragging || this.resizing) {
				this.persistGeom()
			}
			this.dragging = false
			this.resizing = false
			this._dragOrigin = null
			this._resizeOrigin = null
		}
	}
}
</script>

<style scoped>
.GoodsStatsFloat {
	position: fixed;
	z-index: 3200;
	display: flex;
	flex-direction: column;
	min-width: 300px;
	min-height: 180px;
	background: rgba(255, 255, 255, 0.72);
	border: 1px solid rgba(229, 231, 235, 0.85);
	border-radius: 10px;
	box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	overflow: hidden;
	user-select: none;
}

.gsf-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 10px 12px 10px 14px;
	background: rgba(243, 244, 246, 0.72);
	border-bottom: 1px solid rgba(229, 231, 235, 0.75);
	cursor: move;
}

.gsf-title {
	display: flex;
	align-items: baseline;
	gap: 12px;
	min-width: 0;
}

.gsf-title-main {
	font-size: 17px;
	font-weight: 700;
	color: var(--app-text, #1f2937);
}

.gsf-title-sub {
	font-size: 15px;
	color: var(--app-text-secondary, #6b7280);
	white-space: nowrap;
}

.gsf-close {
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	border: none;
	border-radius: 6px;
	background: transparent;
	color: #6b7280;
	font-size: 22px;
	line-height: 1;
	cursor: pointer;
}

.gsf-close:hover {
	background: rgba(229, 231, 235, 0.85);
	color: #111827;
}

.gsf-body {
	flex: 1;
	min-height: 0;
	overflow: auto;
	padding: 12px 14px 16px;
	cursor: default;
}

.gsf-form {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.gsf-row {
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	align-items: center;
	gap: 10px 16px;
	min-height: 30px;
}

.gsf-name {
	min-width: 0;
	font-size: 15px;
	color: var(--app-text, #374151);
	word-break: break-word;
	user-select: text;
}

.gsf-fields {
	display: grid;
	grid-template-columns: 158px 100px;
	align-items: baseline;
	justify-items: end;
	gap: 12px;
	flex-shrink: 0;
}

.gsf-field {
	display: inline-flex;
	align-items: baseline;
	justify-content: flex-end;
	gap: 4px;
	width: 100%;
	min-height: 1.5em;
	white-space: nowrap;
	font-size: 15px;
	user-select: text;
}

.gsf-field-label,
.gsf-field-unit {
	color: var(--app-text-secondary, #6b7280);
}

.num {
	color: var(--app-text, #111827);
	font-size: 1.05em;
	font-weight: 700;
	font-variant-numeric: tabular-nums;
}

.gsf-empty {
	padding: 24px 8px;
	text-align: center;
	color: #9ca3af;
	font-size: 15px;
}

.gsf-resize {
	position: absolute;
	right: 0;
	bottom: 0;
	width: 16px;
	height: 16px;
	cursor: nwse-resize;
	background:
		linear-gradient(135deg, transparent 50%, #9ca3af 50%, #9ca3af 60%, transparent 60%),
		linear-gradient(135deg, transparent 68%, #9ca3af 68%, #9ca3af 78%, transparent 78%);
}

@media (max-width: 768px) {
	.GoodsStatsFloat {
		border-radius: 12px 12px 0 0;
		min-width: 0;
		padding-bottom: env(safe-area-inset-bottom, 0);
	}

	.gsf-header {
		cursor: default;
	}

	.gsf-row {
		grid-template-columns: 1fr;
		gap: 6px;
	}

	.gsf-fields {
		grid-template-columns: 1fr 1fr;
		justify-items: start;
		width: 100%;
	}

	.gsf-resize {
		display: none;
	}
}
</style>
